import React, { useState, useRef, useEffect } from 'react';
import DATA_TEST_IDS from 'chameleon-src/components/assign/AssignManageForm/utils/dataTestIds';
import Button from 'chameleon/ui-stack/ui/base/button';
import InformationalPopover from 'chameleon/ui-stack/ui/popovers/informational-popover';
import { useFormContext } from 'react-hook-form';
import {
  useSelections,
  computeDisplayValue,
  initializeSelectedClasses,
  allStudentsSelectedInitally,
} from './utils';
import ClassItemDropdown from './ClassItemDropdown';
import AlreadyAssignedSection from './ClassItemDropdown/AlreadyAssignedSection';
import { FIELD_NAMES } from '../BasicSettings';
import { KEY_CODES } from 'chameleon/legacy/src/utility/utils';
import DropdownMenuIcon from './DropdownMenuIcon';
import get from 'lodash/get';
import intersection from 'lodash/intersection';
import difference from 'lodash/difference';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

export const SELECT_ALL_TEXT = 'Select All';
export const UNSELECT_ALL_TEXT = 'Unselect All';

const ReadOnlyInput = ({
  id,
  name,
  placeholder,
  onClick,
  onFocus,
  onKeyUp,
  value,
  tabIndex,
  onBlur,
  inputRef,
}) => (
  <input
    data-test-id={DATA_TEST_IDS.INPUT_DEFAULT_VALUE}
    id={id}
    className="h-12 w-full border rounded-full border-gray-500 text-base text-gray-900 px-5 py-1"
    name={name}
    placeholder={placeholder}
    value={value}
    autoComplete="off"
    onClick={onClick}
    onFocus={onFocus}
    onBlur={onBlur}
    onKeyUp={onKeyUp}
    readOnly
    tabIndex={tabIndex}
    ref={inputRef}
  />
);

const ClassesStudentsSelectDropdown = ({
  classes,
  allAssigneesName,
  infoPopupText,
  title,
  id,
  disabledStudents,
  inProgressAssignments,
  router,
  isAssessmentForm,
  studentsBySection,
  isCASDropdown,
  otherActiveSettings,
  isPlaceholder,
}) => {
  const { setValue, getValues, watch } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const watchedClassDropdown = watch(`${id}.${FIELD_NAMES.CLASS_DROPDOWN}`);
  const initialSelectedClasses = initializeSelectedClasses(
    classes,
    disabledStudents,
    inProgressAssignments,
    isCASDropdown,
  );
  const { isAllSelected: isAllSelectedCalculated } =
    allStudentsSelectedInitally(
      classes,
      watchedClassDropdown,
      disabledStudents,
    );
  const { toggleSelection, selectAll } = useSelections(
    classes,
    disabledStudents,
    watchedClassDropdown,
    setValue,
    id,
    setIsAllSelected,
    isAllSelected,
  );

  const [inputValue, setInputValue] = useState(() =>
    computeDisplayValue(
      classes,
      watchedClassDropdown,
      disabledStudents,
      inProgressAssignments,
    ),
  );

  const inputDisplayValueRef = useRef(inputValue);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const lastFocusableElementRef = useRef(null);

  const dynamicInputValueUpdate = (selection) => {
    const newValue = computeDisplayValue(
      classes,
      selection,
      disabledStudents,
      inProgressAssignments,
    );
    setInputValue(newValue);
    inputDisplayValueRef.current = newValue;
  };

  useEffect(() => {
    dynamicInputValueUpdate(watchedClassDropdown);
  }, [watchedClassDropdown]);

  useEffect(() => {
    setIsAllSelected(isAllSelectedCalculated);
  }, [isAllSelectedCalculated]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest(`#${allAssigneesName}`) &&
        !event.target.closest(
          `[data-test-id="${DATA_TEST_IDS.ASSIGN_DROPDOWN_TOGGLE}"]`,
        )
      ) {
        setIsOpen(false);
      }
    };

    /**
     * When the dropdown loads, this gives the main setting dropdown the correct default
     * selections.
     *
     * CAS initial dropdown values are set when it is dynamically created. So
     * - there's no need to do it here
     * - doing it here will wrongly clear the dropdown values when another CAS setting
     * is deleted
     */
    if (!isCASDropdown) {
      setValue(`${id}.${FIELD_NAMES.CLASS_DROPDOWN}`, initialSelectedClasses);
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputFocus = () => {
    setInputValue('');
  };

  const handleInputBlur = () => {
    setInputValue(inputDisplayValueRef.current);
  };

  const handleKeyPress = (e) => {
    if (
      e.target.id === allAssigneesName ||
      document.activeElement === inputRef.current
    ) {
      setIsOpen((prev) => !prev);
    }
    if (
      e.keyCode === KEY_CODES.return ||
      e.keyCode === KEY_CODES.space ||
      e.keyCode === KEY_CODES.tab
    ) {
      const shouldCloseDropdown =
        document.activeElement === lastFocusableElementRef.current;

      if (shouldCloseDropdown) {
        setIsOpen(false);
      }
    }
  };

  const handleCheckboxChange = (classId, studentId = null) => {
    toggleSelection(classId, studentId);
  };

  useEffect(() => {
    otherActiveSettings.map(({ id: settingId }) => {
      const otherDropdown = getValues(
        `${settingId}.${FIELD_NAMES.CLASS_DROPDOWN}`,
      );
      const sharedSections = intersection(
        Object.keys(otherDropdown),
        Object.keys(watchedClassDropdown),
      );

      /**
       * There are no common selected sections between our current update and
       * one of the other settings that we're currently looping through.
       * Don't bother doing other checks since there's no overlap.
       */
      if (sharedSections.length === 0) return;

      /**
       * Loop through each shared section to determine what changes (if any) will need
       * to be done on the other setting we're currently looping through.
       */
      const updatedOtherDropdown = sharedSections.reduce(
        (accumulator, current) => {
          /**
           * We just selected an empty section, and it was selected in another setting
           * so we need to delete this empty section from the other setting
           */
          const isEmptySection =
            get(studentsBySection, current, [0]).length === 0;
          if (isEmptySection) {
            delete accumulator[current];
            return accumulator;
          }

          const otherDropdownStudents = otherDropdown[current].student_ids;
          const currentDropdownStudents =
            watchedClassDropdown[current].student_ids;
          /**
           * Get our student selections here, and make sure to unselect them from the
           * dropdown we're currently looping though.
           */
          const newStudentList = difference(
            otherDropdownStudents,
            currentDropdownStudents,
          );

          /**
           * From the selections we have made in this dropdown, we have made it so that
           * another setting has unselected all the students from this section so don't
           * bother adding the section key back into the dict of selected sections
           */
          if (newStudentList.length === 0) {
            delete accumulator[current];
            return accumulator;
          }

          /**
           * If we're here it means that our student selections are such that only a
           * subset of students needed to be unselected from another dropdown.
           */
          accumulator[current] = { student_ids: newStudentList };
          return accumulator;
        },
        cloneDeep(otherDropdown),
      );

      /**
       * Make the neccessary unselections (if any) in the other active dropdown we're
       * currently looping through.
       *
       * Check if current value is different from the previous to prevent
       * - a recursive loop
       * - unnecessary re-render
       */
      if (!isEqual(otherDropdown, updatedOtherDropdown))
        setValue(
          `${settingId}.${FIELD_NAMES.CLASS_DROPDOWN}`,
          updatedOtherDropdown,
        );
    });
  }, [watchedClassDropdown]);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="relative mb-8">
      <div className="h-8 flex items-center mb-0.8">
        <label className="mr-4 text-base font-medium text-gray-800">
          {title}
        </label>
        {infoPopupText && (
          <InformationalPopover size="medium" defaultOpen={false}>
            <span
              aria-live="assertive"
              aria-label={infoPopupText}
              className="text-base leading-8"
            >
              {infoPopupText}
            </span>
          </InformationalPopover>
        )}
      </div>
      {isPlaceholder ? (
        <div
          data-test-id={DATA_TEST_IDS.PLACEHOLDER_TEXT}
          className="text-gray-500 mt-4 mb-10 italic leading-10"
        >
          Classes appear here
        </div>
      ) : (
        <div className="relative w-full">
          <div className="flex items-center relative">
            <DropdownMenuIcon
              onClick={toggleDropdown}
              className="dropdown-menu-icon"
              dataTestId={DATA_TEST_IDS.ASSIGN_DROPDOWN_TOGGLE}
              iconId="selectbox_arrow_down"
              iconClassName="text-gray-600"
              accessibility={{ ariaHidden: 'true' }}
            />
            <ReadOnlyInput
              id={allAssigneesName}
              name={allAssigneesName}
              placeholder="Select Classes or Students"
              onClick={toggleDropdown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              value={inputValue}
              onKeyUp={handleKeyPress}
              tabIndex="0"
              inputRef={inputRef}
            />
          </div>
        </div>
      )}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute w-full bg-white border border-gray-300 shadow-lg z-10 max-h-60 overflow-auto"
        >
          <Button
            isSecondary
            className="mb-4 mx-4 my-2"
            data-test-id={DATA_TEST_IDS.SELECT_ALL_SECTIONS}
            aria-label={isAllSelected ? UNSELECT_ALL_TEXT : SELECT_ALL_TEXT}
            onClick={selectAll}
            tabIndex="0"
          >
            {isAllSelected ? UNSELECT_ALL_TEXT : SELECT_ALL_TEXT}
          </Button>
          <div
            tabIndex="0"
            role="button"
            aria-label="Last focusable element"
            ref={lastFocusableElementRef}
            onKeyDown={handleKeyPress}
            data-test-id={DATA_TEST_IDS.CLASSES_DROPDOWN_LAST_FOCUSABLE_ELEMENT}
          >
            {classes.map((cls) => (
              <ClassItemDropdown
                key={cls.id}
                cls={cls}
                handleCheckboxChange={handleCheckboxChange}
                settingId={id}
                toggleClass={(classId) => toggleSelection(classId, true)}
                disabledStudents={disabledStudents}
                otherActiveSettings={otherActiveSettings}
                getValues={getValues}
              />
            ))}
          </div>
          {(inProgressAssignments || []).length > 0 && (
            <AlreadyAssignedSection
              inProgressAssignments={inProgressAssignments}
              router={router}
              studentsBySection={studentsBySection}
              isAssessmentForm={isAssessmentForm}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ClassesStudentsSelectDropdown;
